import {
  CircleLayerSpecification,
  FillLayerSpecification,
  LineLayerSpecification,
  SymbolLayerSpecification,
} from 'mapbox-gl';

export const ROAD_MAP_TYPE = 'roadmap';
export const SATELLITE_MAP_TYPE = 'satellite';

export const MAP_CONFIGURATION = {
  [ROAD_MAP_TYPE]: {
    style: 'mapbox://styles/mapbox/streets-v12',
  },
  [SATELLITE_MAP_TYPE]: {
    style: 'mapbox://styles/mapbox/satellite-streets-v12',
  },
};

export const TRAFFIC_LAYER_STYLE: LineLayerSpecification = {
  id: 'traffic',
  source: 'mapbox-traffic',
  'source-layer': 'traffic',
  type: 'line',
  paint: {
    'line-width': 1.5,
    'line-color': [
      'case',
      ['==', 'low', ['get', 'congestion']],
      '#aab7ef',
      ['==', 'moderate', ['get', 'congestion']],
      '#4264fb',
      ['==', 'heavy', ['get', 'congestion']],
      '#ee4e8b',
      ['==', 'severe', ['get', 'congestion']],
      '#b43b71',
      '#000000',
    ],
  },
};

export const SPOTS_LAYER_STYLE: SymbolLayerSpecification = {
  id: 'spots-layer',
  source: 'spots',
  type: 'symbol',
  layout: {
    'icon-image': ['get', 'icon'], // reference the image
    'icon-size': 0.5,
  },
  minzoom: 12,
  maxzoom: 19,
};

export const SHARED_FENCES_FILL_LAYER_STYLE: Omit<FillLayerSpecification, 'id'> = {
  type: 'fill',
  source: 'fences',
  paint: {
    'fill-color': '#000',
    'fill-opacity': 0.3,
  },
  minzoom: 6,
  maxzoom: 19,
};

export const SHARED_FENCES_OUTLINE_LAYER_STYLE: Omit<LineLayerSpecification, 'id'> = {
  type: 'line',
  source: 'fences',
  paint: {
    'line-color': '#000',
    'line-width': 2,
  },
  minzoom: 6,
  maxzoom: 19,
};

export const SHARED_FENCES_NAME_LAYER_STYLE: Omit<SymbolLayerSpecification, 'id'> = {
  type: 'symbol',
  source: 'fences',
  layout: {
    'text-field': ['get', 'name'],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
    'text-size': 11,
    'text-letter-spacing': 0.05,
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#dedede',
    'text-halo-width': 1,
  },
  minzoom: 12,
  maxzoom: 19,
};

export const FENCES_LAYER_STYLE: FillLayerSpecification = {
  id: 'fences',
  ...SHARED_FENCES_FILL_LAYER_STYLE,
};

export const FENCES_OUTLINE_LAYER_STYLE: LineLayerSpecification = {
  id: 'fences-outline',
  ...SHARED_FENCES_OUTLINE_LAYER_STYLE,
};

export const FENCES_NAME_LAYER_STYLE: SymbolLayerSpecification = {
  id: 'fences-name',
  ...SHARED_FENCES_NAME_LAYER_STYLE,
  maxzoom: 19,
};

const SHARED_LINE_LAYER_STYLE: Omit<LineLayerSpecification, 'id' | 'source'> = {
  type: 'line',
  layout: {
    'line-join': 'round',
    'line-cap': 'round',
  },
  paint: {
    'line-color': '#000',
    'line-width': 6,
  },
};

export const TRACKING_LAYER_STYLE: LineLayerSpecification = {
  id: 'track',
  source: 'tracking',
  ...SHARED_LINE_LAYER_STYLE,
};

const SHARED_ARROW_LAYER_STYLE: Omit<SymbolLayerSpecification, 'id' | 'source'> = {
  type: 'symbol',
  layout: {
    'symbol-placement': 'line',
    'symbol-spacing': 3,
    'icon-image': 'arrow',
    'icon-size': 0.2,
  },
  paint: {
    'icon-opacity': 1,
  },
};

export const TRACKING_ARROW_LAYER_STYLE: SymbolLayerSpecification = {
  id: 'tracking-arrow',
  source: 'tracking',
  ...SHARED_ARROW_LAYER_STYLE,
};

export const PLACES_LAYER_STYLE: CircleLayerSpecification = {
  id: 'place',
  type: 'circle',
  source: 'circle',
  paint: {
    'circle-color': '#ffffff',
    'circle-radius': 2.5,
    'circle-stroke-color': '#000000',
    'circle-stroke-width': 6,
  },
  minzoom: 12,
  maxzoom: 19,
};

export const PLACES_NAME_LAYER_STYLE: SymbolLayerSpecification = {
  id: 'places-name',
  source: 'places',
  type: 'symbol',
  layout: {
    'text-field': ['get', 'name'],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
    'text-size': 11,
    'text-letter-spacing': 0.05,
    'text-offset': [0, 1.5],
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#dedede',
    'text-halo-width': 1,
  },
  minzoom: 12,
  maxzoom: 19,
};

export const ROUTING_LAYER_STYLE: LineLayerSpecification = {
  id: 'route',
  source: 'routing',
  ...SHARED_LINE_LAYER_STYLE,
};

export const ROUTING_ARROW_LAYER_STYLE: SymbolLayerSpecification = {
  id: 'route-arrow',
  source: 'routing',
  ...SHARED_ARROW_LAYER_STYLE,
};

export const ROUTING_EVENTS_LAYER_STYLE: CircleLayerSpecification = {
  id: 'route-event',
  type: 'circle',
  source: 'route-events',
  paint: {
    'circle-color': '#ffffff',
    'circle-radius': 2.5,
    'circle-stroke-color': '#000000',
    'circle-stroke-width': 6,
  },
  minzoom: 12,
  maxzoom: 19,
};

export const PATH_LAYER_STYLE: LineLayerSpecification = {
  id: 'path-layer',
  source: 'path',
  ...SHARED_LINE_LAYER_STYLE,
};

export const PATH_ARROW_LAYER_STYLE: SymbolLayerSpecification = {
  id: 'path-arrow',
  source: 'path',
  ...SHARED_ARROW_LAYER_STYLE,
};

export const PATH_POINTS_LAYER_STYLE: CircleLayerSpecification = {
  id: 'path-point',
  type: 'circle',
  source: 'path',
  paint: {
    'circle-color': '#ffffff',
    'circle-radius': 2.5,
    'circle-stroke-color': '#000000',
    'circle-stroke-width': 4,
  },
  minzoom: 12,
  maxzoom: 19,
};

export const RISK_ZONES_LAYER_STYLE: FillLayerSpecification = {
  id: 'risk-zones',
  source: 'riskZones',
  type: 'fill',
  paint: {
    'fill-color': '#e11900',
    'fill-opacity': 0.3,
  },
  minzoom: 6,
  maxzoom: 19,
};

export const RISK_ZONES_OUTLINE_LAYER_STYLE: LineLayerSpecification = {
  id: 'risk-zones-outline',
  source: 'riskZones',
  type: 'line',
  paint: {
    'line-color': '#e11900',
    'line-width': 2,
  },
  minzoom: 6,
  maxzoom: 19,
};

export const RISK_ZONES_NAME_LAYER_STYLE: SymbolLayerSpecification = {
  id: 'risk-zones-name',
  source: 'riskZones',
  type: 'symbol',
  layout: {
    'text-field': ['get', 'name'],
    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
    'text-size': 11,
    'text-letter-spacing': 0.05,
  },
  paint: {
    'text-color': '#000',
    'text-halo-color': '#dedede',
    'text-halo-width': 1,
  },
  minzoom: 12,
  maxzoom: 19,
};

export const DRAW_MODE_POLYGON = 'draw_polygon';
export const DRAW_MODE_CIRCLE = 'circle';
export const DRAW_MODE_RECTANGLE = 'draw_rectangle';

export const TELEMETRY_LAYER_STYLE: LineLayerSpecification = {
  id: 'telemetry',
  source: 'telemetry',
  ...SHARED_LINE_LAYER_STYLE,
};

export const TELEMETRY_ARROW_LAYER_STYLE: SymbolLayerSpecification = {
  id: 'telemetry-arrow',
  source: 'telemetry',
  ...SHARED_ARROW_LAYER_STYLE,
};

export const DELIVERY_FENCE_LAYER_STYLE: FillLayerSpecification = {
  ...SHARED_FENCES_FILL_LAYER_STYLE,
  id: 'delivery-fence',
};

export const DELIVERY_FENCE_OUTLINE_LAYER_STYLE: LineLayerSpecification = {
  ...SHARED_FENCES_OUTLINE_LAYER_STYLE,
  id: 'delivery-fence-outline',
};

export const DELIVERY_FENCE_NAME_LAYER_STYLE: SymbolLayerSpecification = {
  ...SHARED_FENCES_NAME_LAYER_STYLE,
  id: 'delivery-fence-name',
};

export const DELIVERY_FENCES_LAYER_STYLE: FillLayerSpecification = {
  ...SHARED_FENCES_FILL_LAYER_STYLE,
  id: 'delivery-fences',
  paint: {
    'fill-color': '#EAA037',
    'fill-opacity': 0.3,
  },
};

export const DELIVERY_FENCES_OUTLINE_LAYER_STYLE: LineLayerSpecification = {
  ...SHARED_FENCES_OUTLINE_LAYER_STYLE,
  id: 'delivery-fences-outline',
  paint: {
    'line-color': '#EE5B25',
    'line-width': 2,
  },
};

export const DELIVERY_FENCES_NAME_LAYER_STYLE: SymbolLayerSpecification = {
  ...SHARED_FENCES_NAME_LAYER_STYLE,
  id: 'delivery-fences-name',
  paint: {
    'text-color': '#000',
    'text-halo-color': '#dedede',
    'text-halo-width': 1,
  },
};
